import { Sparkles, Check, Ban, CheckCircle2, Loader2, ArrowRight, AlertTriangle, ShieldCheck, Eye } from "lucide-react";

const TYPE_LABEL = {
  update_site_content: "Sitio · Contenido", set_site_image: "Sitio · Imagen",
  create_service: "Catálogo · Nuevo servicio", update_service: "Catálogo · Editar servicio",
  update_pricing: "Catálogo · Precios", apply_discount: "Catálogo · Descuento", remove_discount: "Catálogo · Quitar descuento",
  deactivate_service: "Catálogo · Ocultar servicio", activate_service: "Catálogo · Mostrar servicio",
  delete_service: "Catálogo · Eliminar servicio", create_category: "Catálogo · Nueva categoría", update_category: "Catálogo · Editar categoría",
  update_system_config: "Configuración del sistema", update_payment_method: "Configuración · Método de pago",
  create_payment_method: "Configuración · Nuevo método de pago",
  send_message: "Mensaje a cliente", block_user: "Bloquear cliente", unblock_user: "Desbloquear cliente", grant_horoscope: "Acceso horóscopo",
};

const DESTRUCTIVE = new Set(["delete_service", "block_user", "deactivate_service"]);

export const AssistantActionCard = ({ action, busy, onConfirm, onCancel }) => {
  const destructive = DESTRUCTIVE.has(action.type);
  return (
    <div
      data-testid={`admin-assistant-action-${action.id}`}
      className={`mt-2 rounded-xl border p-3 ${destructive ? "border-red-400/40 bg-red-500/5" : "border-amber-400/40 bg-amber-500/5"}`}
    >
      <div className={`text-[10px] uppercase tracking-widest mb-1 flex items-center gap-1 ${destructive ? "text-red-300" : "text-amber-300"}`}>
        {destructive ? <AlertTriangle className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
        Confirmar · {TYPE_LABEL[action.type] || action.type}
      </div>
      <div className="text-[13px] text-neutral-100 mb-2" data-testid="admin-assistant-action-summary">{action.summary}</div>

      {action.image_url && (
        <img src={action.image_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${action.image_url}` : action.image_url} alt="Vista previa de imagen" className="w-full rounded-lg mb-2 max-h-40 object-cover" loading="lazy" />
      )}
      {action.preview_url && action.status === "pending" && (
        <a
          href={action.preview_url}
          target="_blank"
          rel="noreferrer"
          data-testid={`admin-assistant-preview-${action.id}`}
          className="mb-2.5 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-full border border-sky-400/40 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20"
        >
          <Eye className="w-3.5 h-3.5" /> Ver vista previa en el sitio
        </a>
      )}

      {(action.details || []).length > 0 && (
        <div className="mb-2.5 rounded-lg bg-black/40 border border-white/10 divide-y divide-white/5" data-testid="admin-assistant-action-details">
          {action.details.map((d, i) => (
            <div key={i} className="px-2.5 py-1.5 text-[12px] flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wider text-neutral-500">{d.label}</span>
              <span className="flex items-start gap-1.5 flex-wrap">
                {d.before !== undefined && d.before !== null && (
                  <>
                    <span className="text-neutral-400 line-through break-all">{String(d.before)}</span>
                    <ArrowRight className="w-3 h-3 text-[#d4af37] mt-0.5 shrink-0" />
                  </>
                )}
                <span className="text-white break-all">{String(d.after ?? "—")}</span>
              </span>
            </div>
          ))}
        </div>
      )}

      {action.status === "done" ? (
        <div className="text-[12px] text-emerald-300 flex items-center gap-1" data-testid="admin-assistant-action-done">
          <CheckCircle2 className="w-3.5 h-3.5" /> Ejecutada · registrada en bitácora
        </div>
      ) : action.status === "cancelled" ? (
        <div className="text-[12px] text-neutral-500 flex items-center gap-1"><Ban className="w-3.5 h-3.5" /> Cancelada</div>
      ) : action.status === "failed" ? (
        <div className="text-[12px] text-red-300 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Falló al ejecutar</div>
      ) : (
        <div className="flex gap-2">
          <button
            data-testid={`admin-assistant-confirm-${action.id}`}
            onClick={() => onConfirm(action)}
            disabled={busy}
            className={`flex-1 py-2 rounded-full font-bold text-[11px] uppercase tracking-widest flex items-center justify-center gap-1 disabled:opacity-50 ${destructive ? "bg-red-500 hover:bg-red-400 text-white" : "bg-emerald-500 hover:bg-emerald-400 text-black"}`}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : destructive ? <ShieldCheck className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
            {destructive ? "Sí, confirmar" : "Confirmar"}
          </button>
          <button
            data-testid={`admin-assistant-cancel-${action.id}`}
            onClick={() => onCancel(action)}
            disabled={busy}
            className="flex-1 py-2 rounded-full border border-white/15 text-neutral-300 hover:bg-white/5 text-[11px] uppercase tracking-widest flex items-center justify-center gap-1"
          >
            <Ban className="w-3.5 h-3.5" /> Cancelar
          </button>
        </div>
      )}
    </div>
  );
};

export default AssistantActionCard;
